import { useState } from 'react';
import {
  MODAL_OPEN_ANIMATION_MS,
  SHEET_CLOSE_ANIMATION_MS,
} from '@/components/Modal/constants';
import { useElementHeight } from '@/hooks/useElementHeight';
import { useEvaluation } from '@/hooks/useEvaluation';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useIsOpenAnimationDone } from '@/hooks/useIsOpenAnimationDone';
import { useScrollFadeProgress } from '@/hooks/useScrollFadeProgress';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import type { Attachment } from '@/types/attachment';
import { LessonDetailsMode, ModalVariant } from '@/types/components';
import type { DiaryLesson, LessonHeading } from '@/types/diary';
import type { EvaluationDetail } from '@/types/overview';

const SHEET_LOADING_HEIGHT_PX = 160;
const SHEET_HANDLE_HEIGHT_PX = 19;

export type LessonDetailsParams =
  | {
      mode: LessonDetailsMode.Lesson;
      lesson: DiaryLesson;
      date: string;
      onClose: () => void;
    }
  | {
      mode: LessonDetailsMode.Evaluation;
      evaluationId: number;
      onClose: () => void;
    };

const getHeading = (
  params: LessonDetailsParams,
  evaluation: EvaluationDetail | undefined,
): LessonHeading | undefined => {
  if (params.mode === LessonDetailsMode.Lesson) {
    const { lesson, date } = params;

    return {
      lessonNumber: lesson.lessonNumber ?? null,
      title: lesson.className,
      roomNumber: lesson.roomNumber,
      date,
    };
  }

  if (!evaluation) {
    return undefined;
  }

  const { lessonNumberInDiary, disciplineName, lessonDate } = evaluation.lesson;

  return {
    lessonNumber: lessonNumberInDiary,
    title: disciplineName,
    date: lessonDate,
  };
};

export const useLessonDetailsModal = (params: LessonDetailsParams) => {
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(
    null,
  );
  const [testReportId, setTestReportId] = useState<number | null>(null);
  const [homeTaskId, setHomeTaskId] = useState<number | null>(null);
  const [hasOpenedSubView, setHasOpenedSubView] = useState(false);
  const [isSheetClosing, setIsSheetClosing] = useState(false);

  const isLessonMode = params.mode === LessonDetailsMode.Lesson;
  const lesson = isLessonMode ? params.lesson : null;
  const evaluationId = isLessonMode
    ? params.lesson.evaluations[0]?.id
    : params.evaluationId;

  const { progress, handleScroll } = useScrollFadeProgress();
  const { sheetRef, swipeProps } = useSwipeToClose(params.onClose);
  const isMobile = useIsMobile();
  const { ref: contentRef, height: contentHeight } = useElementHeight();
  const { data: evaluation, isLoading } = useEvaluation(evaluationId);
  const [isDataCachedOnOpen] = useState(!isLoading);
  const isOpenAnimationDone = useIsOpenAnimationDone(MODAL_OPEN_ANIMATION_MS);

  const isContentReady =
    !isLoading && (isDataCachedOnOpen || isOpenAnimationDone);
  const isSubViewOpen = testReportId !== null || homeTaskId !== null;
  const isBottomSheet = !isLessonMode;
  const isAnimatedSheet = isBottomSheet && isMobile;
  const sheetBodyHeight =
    isContentReady && contentHeight ? contentHeight : SHEET_LOADING_HEIGHT_PX;
  const sheetBodyStyle = isAnimatedSheet
    ? {
        height: `min(${sheetBodyHeight}px, calc(90svh - ${SHEET_HANDLE_HEIGHT_PX}px))`,
      }
    : undefined;

  const showTestReport = (testId: number) => {
    setHasOpenedSubView(true);
    setTestReportId(testId);
  };

  const openTestReport = (testId: number) => {
    if (!isAnimatedSheet) {
      showTestReport(testId);
      return;
    }

    setIsSheetClosing(true);
    setTimeout(() => showTestReport(testId), SHEET_CLOSE_ANIMATION_MS);
  };

  const openHomeTask = (id: number) => {
    setHasOpenedSubView(true);
    setHomeTaskId(id);
  };

  return {
    lesson,
    evaluation,
    heading: getHeading(params, evaluation),
    videoMeetings: lesson?.videoMeetings ?? [],
    isScrolled: progress > 0.5,
    isContentReady,
    isNotFound: isContentReady && !lesson && !evaluation,
    isBottomSheet,
    isAnimatedSheet,
    isSheetClosing,
    isReturningFromSubView: hasOpenedSubView && !isSubViewOpen,
    modalVariant: isBottomSheet
      ? ModalVariant.BottomSheet
      : ModalVariant.Default,
    sheetBodyStyle,
    sheetRef,
    swipeProps,
    contentRef,
    handleScroll,
    testReportId,
    homeTaskId,
    previewAttachment,
    setPreviewAttachment,
    openTestReport,
    openHomeTask,
    closeTestReport: () => setTestReportId(null),
    closeHomeTask: () => setHomeTaskId(null),
  };
};
